import React, { useEffect, useRef } from "react";
import { Column, Icon, Row, Text } from "@amzn/meridian";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/store";
import chevronLeftLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import { COLORS } from "src/constants/colors";
import CameraFrame from "./cameraFrame";
import BarcodeFrame from "../barcodeScanner/BarcodeFrame";
import { setSelectedInspectionTemplate } from "src/store/inspectionTemplateSlice";
import {
  setStreamingMode,
  setImageCapturingMode,
  setCameraOpen,
  setStreamingDevMode,
} from "src/store/inspectionCaptureSlice";
import SubmitReport from "./submitReport";

const Inspection = () => {
  const { inspectionTemplates, selectedInspectionTemplate } = useAppSelector((state) => state.inspectionTemplate);
  const { activeQuestion, imageCapturingMode, readyToSubmitReport, streamingMode, cameraOpen, screenWidth, screenHeight, selectedCameraDeviceId, barcodeScanningMode } = useAppSelector((state) => state.inspectionCapture);
  const [activeQuestionIndex] = React.useState(0);
  
  const streamRef = useRef<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const navigate = useNavigate();
  const { templateId } = useParams();
  const dispatch = useAppDispatch();
  const totalQuestions = selectedInspectionTemplate?.questions?.length;

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw Error("Camera not supported in this browser");
    }
    try {
      const videoConstraints: MediaTrackConstraints = {
        width: { ideal: screenWidth },
        height: { ideal: screenHeight },
      };
      
      // Use deviceId if specified, otherwise use facingMode
      if (selectedCameraDeviceId) {
        videoConstraints.deviceId = { exact: selectedCameraDeviceId };
      } else {
        videoConstraints.facingMode = "environment";
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: false,
      });
      streamRef.current = stream;
      if (!videoRef.current) {
        return;
      }
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play().catch(() => {});
    } catch (e: any) {
      const name = e?.name || "";
      if (name === "NotAllowedError") {
        throw Error("Camera access was denied. Allow camera in settings.");
      } else if (name === "NotFoundError") {
        throw Error("No camera found on this device.");
      } else {
        throw Error(`Camera error: ${e?.message || String(e)}`);
      }
    }
  };
  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
  };
  
  useEffect(() => {
    // Start camera when entering the page with a mode selected (but not in barcode scanning mode)
    if (!barcodeScanningMode && (imageCapturingMode || streamingMode)) {
      // Retry mechanism for when video element isn't ready yet
      const attemptStartCamera = async (retries = 0) => {
        if (retries > 10) {
          console.error("Failed to start camera after 10 retries");
          return;
        }
        
        if (!videoRef.current) {
          setTimeout(() => attemptStartCamera(retries + 1), 200);
          return;
        }
        
        try {
          await startCamera();
          console.log("Camera started successfully");
        } catch (error) {
          console.error("Error starting camera:", error);
        }
      };
      
      // Always stop camera first to ensure clean restart
      console.log("Stopping camera for restart...");
      stopCamera();
      // Small delay to ensure camera is fully released
      setTimeout(() => {
        console.log("Attempting to start camera...");
        attemptStartCamera();
      }, 100);
    }
    
    return () => {
      stopCamera();
    };
  }, [imageCapturingMode, streamingMode, barcodeScanningMode]);
  
  // Effect to handle readyToSubmitReport changes
  useEffect(() => {
    if (readyToSubmitReport) {
      stopCamera();
    }
  }, [readyToSubmitReport]);

  useEffect(() => {
    if (!selectedInspectionTemplate && templateId) {
      const template = inspectionTemplates.find(
        (temp) => temp.templateId === templateId
      );
      if (template) {
        dispatch(setSelectedInspectionTemplate(template));
        navigate(`/templates`);
      }
    }
  }, [selectedInspectionTemplate, templateId, inspectionTemplates]);

  // Handle returning from camera to questionsPage
  const handleBackToList = () => {
    stopCamera();
    dispatch(setCameraOpen(false));
    dispatch(setImageCapturingMode(false));
    dispatch(setStreamingMode(false));
    dispatch(setStreamingDevMode(false));
    navigate(`/templates/${selectedInspectionTemplate?.templateId}`);
  };

  // Redirect to questionsPage if no camera mode is active
  useEffect(() => {
    if (!imageCapturingMode && !streamingMode && !cameraOpen && !readyToSubmitReport && !barcodeScanningMode) {
      navigate(`/templates/${selectedInspectionTemplate?.templateId}`);
    }
  }, [imageCapturingMode, streamingMode, cameraOpen, readyToSubmitReport, barcodeScanningMode]);

  return readyToSubmitReport ? (
    <SubmitReport />
  ) : (
    <Column
      className="inspection-page"
      height="100vh"
      alignmentVertical="justify"
      backgroundColor={COLORS.VERY_DARK_GRAY}
    >
      <Column
        width="100%"
        spacingInset="600 500 400"
        className="inspection-info-wrapper"
      >
        <Row>
          <div
            style={{ cursor: "pointer", marginLeft: "50px" }}
            onClick={handleBackToList}
          >
            <Icon tokens={chevronLeftLargeTokens} color="inverted" />
          </div>
          {selectedInspectionTemplate?.templateName && (
            <Text color="inverted" type="b100">
              {selectedInspectionTemplate?.templateName}
            </Text>
          )}
        </Row>
        <Column spacing={"300"}>
          <Row spacingInset={"200 none"}>
            <Text color="inverted">{ barcodeScanningMode ? `Scan Drum Barcode` : `${activeQuestion?.questionText}`}</Text>
          </Row>
        </Column>
      </Column>
      {barcodeScanningMode ? (
      <BarcodeFrame idealHeight={screenHeight > 0 ? screenHeight : 1080} idealWidth={screenWidth > 0 ? screenWidth : 1920} />) :  (<CameraFrame onCapture={() => {}} targetClasses={[]} />)}
    </Column>
  );
};

export default Inspection;
